import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { 
  Building2, Landmark, Shield, Briefcase, GitBranch, Plus, Loader2, RefreshCw
} from 'lucide-react';

const entityIcons = {
  HOLDING: Landmark,
  LLC: Building2,
  TRUST: Shield,
  CORPORATION: Briefcase,
  SUBSIDIARY: GitBranch
};

// Custom Node Component
function EntityNode({ data }) {
  const Icon = entityIcons[data.type] || Building2;
  
  return (
    <div 
      className="px-4 py-3 rounded-xl border-2 shadow-lg min-w-[200px]"
      style={{ 
        backgroundColor: '#0f172a',
        borderColor: data.color,
        borderLeftWidth: '4px'
      }}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3" style={{ backgroundColor: data.color }} />
      
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${data.color}33` }}
        >
          <Icon className="w-5 h-5" style={{ color: data.color }} />
        </div>
        <div>
          <p className="font-semibold text-white text-sm">{data.label}</p>
          <p className="text-xs text-slate-400">{data.jurisdiction}</p>
        </div>
      </div>
      
      <div className="mt-2 flex items-center justify-between">
        <span 
          className="text-xs px-2 py-1 rounded-full font-medium"
          style={{ backgroundColor: `${data.color}33`, color: data.color }}
        >
          {data.type}
        </span>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="w-3 h-3" style={{ backgroundColor: data.color }} />
    </div>
  );
}

const nodeTypes = {
  entityNode: EntityNode,
};

export default function StructureVisualizationPage() {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStructure();
  }, []);

  const loadStructure = async () => {
    setLoading(true);
    try {
      const res = await api.get('/structure/tree');
      setNodes(res.data.nodes || []);
      setEdges(res.data.edges || []);
    } catch (err) {
      console.error('Failed to load structure:', err);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-120px)]" data-testid="structure-visualization-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">Structure Visualization</h1>
            <p className="text-slate-400 mt-1">Interactive view of your corporate hierarchy</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={loadStructure}
              variant="outline"
              className="border-slate-700 text-slate-300"
              data-testid="refresh-structure-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => navigate('/entities/new')}
              className="gradient-accent hover:opacity-90"
              data-testid="add-entity-structure-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Entity
            </Button>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div className="h-full rounded-xl border border-slate-800 overflow-hidden bg-slate-950">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Building2 className="w-16 h-16 text-slate-600 mb-4" />
              <h3 className="text-lg font-medium mb-2">No entities to visualize</h3>
              <p className="text-slate-400 mb-6">Create entities to see your corporate structure</p>
              <Button
                onClick={() => navigate('/entities/new')}
                className="gradient-accent hover:opacity-90"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Entity
              </Button>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              attributionPosition="bottom-left"
              style={{ backgroundColor: '#020617' }}
            >
              <Controls 
                className="bg-slate-900 border-slate-700 rounded-lg"
                style={{ button: { backgroundColor: '#1e293b', color: '#94a3b8', borderColor: '#334155' } }}
              />
              <MiniMap 
                style={{ 
                  backgroundColor: '#0f172a',
                  border: '1px solid #1e293b',
                  borderRadius: '8px'
                }}
                nodeColor={(node) => node.data?.color || '#64748b'}
                maskColor="rgba(0, 0, 0, 0.8)"
              />
              <Background variant="dots" gap={20} size={1} color="#1e293b" />
            </ReactFlow>
          )}
        </div>

        {/* Legend */}
        {nodes.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-4">
            {[
              { type: 'HOLDING', color: '#f59e0b', label: 'Holding Company' },
              { type: 'LLC', color: '#3b82f6', label: 'LLC' },
              { type: 'TRUST', color: '#10b981', label: 'Trust' },
              { type: 'CORPORATION', color: '#8b5cf6', label: 'Corporation' },
              { type: 'SUBSIDIARY', color: '#ec4899', label: 'Subsidiary' }
            ].map(item => {
              const Icon = entityIcons[item.type];
              return (
                <div key={item.type} className="flex items-center gap-2 text-sm text-slate-400">
                  <div 
                    className="w-6 h-6 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${item.color}33` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: item.color }} />
                  </div>
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
